import { router } from '@inertiajs/vue3'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { MenuItem } from '@/components/molecules/menu/twn-menu-popup/TwnMenuPopup.types'
import type {
  ItemName,
  ItemSegment,
  PageType
} from '@/domains/admin-panel/store/use-breadcrumb-store/useBreadcrumbStore.types'

import { useMenuStore } from '@/domains/admin-panel/store/use-menu-store/useMenuStore.ts'

const toCrumb = (segment: ItemSegment | null): MenuItem | null => {
  if (!segment) return null
  if (typeof segment === 'string') return { label: segment, url: undefined }

  return segment.label ? { label: segment.label, url: segment.url } : null
}

export const useBreadcrumbStore = defineStore('breadcrumb', () => {
  // State
  const itemName = ref<ItemName>(null)
  const pageType = ref<PageType>(undefined)

  // Get menu store
  const menuStore = useMenuStore()

  // The last query each path was shown with, so a crumb back to a list returns to the filters left on it.
  const queryByPath = ref<Record<string, string>>({})

  const rememberQuery = (url: string) => {
    const { pathname, search } = new URL(url, window.location.origin)
    queryByPath.value[pathname] = search
  }

  // The first page can render before this store exists, and then its navigate event has already passed.
  rememberQuery(window.location.href)
  router.on('navigate', event => rememberQuery(event.detail.page.url))

  // A crumb that carries its own query keeps it; a bare path gets back the query it was last shown with.
  const withRememberedQuery = (crumb: MenuItem): MenuItem => {
    if (!crumb.url) return crumb

    const { pathname, search } = new URL(crumb.url, window.location.origin)
    const rememberedQuery = queryByPath.value[pathname]
    const carriesOwnQuery = search !== ''

    if (carriesOwnQuery || !rememberedQuery) return crumb

    return { ...crumb, url: `${crumb.url}${rememberedQuery}` }
  }

  // Computed property that returns the complete breadcrumb items array
  const breadcrumbItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = []

    const currentPage = menuStore.currentActiveNav?.items?.find(item => item.active)
    // A page named like its space would read "Products / Products"; the space crumb says it once, with its link.
    const isPageNamedLikeSpace = !!currentPage && currentPage.name === menuStore.currentActiveNav?.name

    // Add category (from currentActiveNav)
    if (menuStore.currentActiveNav?.name) {
      items.push({
        label: menuStore.currentActiveNav.name,
        url: menuStore.currentActiveNav.path || (isPageNamedLikeSpace ? currentPage?.path : undefined) || undefined
      })
    }

    // Add section (from currentPage)
    if (currentPage && !isPageNamedLikeSpace) {
      items.push({
        label: currentPage.name,
        url: currentPage.path || undefined
      })
    }

    // Add item name(s). Single segment → one crumb; array → one crumb per element
    // so pages with hierarchical context (e.g. Configuration) can render the
    // full path. A segment with a url links to its page. Empty array or
    // null/empty label is treated as "no item crumb".
    const segments = Array.isArray(itemName.value) ? itemName.value : [itemName.value]
    for (const segment of segments) {
      const crumb = toCrumb(segment)
      if (crumb) items.push(crumb)
    }

    // Add page type if available
    if (pageType.value) {
      items.push({
        label: pageType.value,
        url: undefined
      })
    }

    return items.map(withRememberedQuery)
  })

  // The page header names the page by its item: a trailing "Edit" adds nothing once "Demo Voucher" is shown.
  const headerItems = computed<MenuItem[]>(() => {
    const segments = Array.isArray(itemName.value) ? itemName.value : [itemName.value]
    const hasItemCrumb = segments.some(segment => toCrumb(segment) !== null)

    return hasItemCrumb && pageType.value ? breadcrumbItems.value.slice(0, -1) : breadcrumbItems.value
  })

  // Actions
  const setItemName = (name: ItemName) => {
    itemName.value = name
  }

  const clearItemName = () => {
    itemName.value = null
  }

  const setPageType = (type: PageType) => {
    pageType.value = type
  }

  const clearPageType = () => {
    pageType.value = undefined
  }

  return {
    // State
    itemName,
    pageType,
    breadcrumbItems,
    headerItems,

    // Actions
    setItemName,
    clearItemName,
    setPageType,
    clearPageType
  }
})
